import React, { useCallback, useEffect, useState } from 'react'
import { Table } from 'react-bootstrap'
import { browserName } from 'react-device-detect'
import { NavigateFunction, useNavigate } from 'react-router-dom'

import { useDispatch } from '../../../modules/store/customDispatch'
import { useSelector } from '../../../modules/store/customSelector'
import { Column } from '../../../modules/store/partList/partListReducer'
import { storeDispatchTypes } from '../../../modules/store/storeDispatchTypes'
import { adminRouteAlias, getAdminNavigationPath } from '../../../router/adminRouteAlias'
import { Car } from '../../../types/car/carTypes'
import { SortFieldCars, SortIconType } from '../../../types/common/commonTypes'
import { countDaysInStock, formatDate, num_word } from '../../../utils/converters'
import { colWidth } from '../../../utils/stylePatches'
import Badge from '../../_atoms/Badge/Badge'
import CellSoldPercent from '../../_atoms/CellSoldPercent/CellSoldPercent'
import ListSkeleton from '../../_atoms/ListSkeleton/ListSkeleton'
import ColumnSort from '../../_molecules/ColumnSort/ColumnSort'
import TableHeadBodyTypeSearchCars from '../../_molecules/TableHeadBodyTypeSearchCars/TableHeadBodyTypeSearchCars'
import TableHeadMarkSearchCars from '../../_molecules/TableHeadMarkSearchCars/TableHeadMarkSearchCars'
import TableHeadMileageRangeCars from '../../_molecules/TableHeadMileageRangeCars/TableHeadMileageRangeCars'
import TableHeadModelSearchCars from '../../_molecules/TableHeadModelSearchCars/TableHeadModelSearchCars'
import TableHeadModificationSearchCars from '../../_molecules/TableHeadModificationSearchCars/TableHeadModificationSearchCars'
import TableHeadPartsCountRangeCars from '../../_molecules/TableHeadPartsCountRangeCars/TableHeadPartsCountRangeCars'
import TableHeadPriceRangeCars from '../../_molecules/TableHeadPriceRangeCars/TableHeadPriceRangeCars'
import CellActionsCars from '../CellActionsCars/CellActionsCars'
import CellThumbCarousel from '../CellThumbCarousel/CellThumbCarousel'

import styles from './styles.module.scss'

interface RenderTableProps {
    columns: Column[]
    cars: Car[]
    isLoading: boolean
    openEditModal?: (car: Car) => void
    openDeleteModal?: (car: Car) => void
}

export const TableCarList = ({ columns, cars, isLoading, openEditModal, openDeleteModal }: RenderTableProps) => {
    const dispatch = useDispatch()

    const [localColumns, setLocalColumns] = useState<Column[]>([])

    const canShowFinanceInfo = useSelector(state => state.userData.perms?.canShowFinanceInfo)
    const mileageFrom = useSelector(state => state.carList.filters.mileageRange?.from)
    const mileageTo = useSelector(state => state.carList.filters.mileageRange?.to)
    const priceFrom = useSelector(state => state.carList.filters.priceRange?.from)
    const priceTo = useSelector(state => state.carList.filters.priceRange?.to)
    const partsCntFrom = useSelector(state => state.carList.filters.partsCntRange?.from)
    const partsCntTo = useSelector(state => state.carList.filters.partsCntRange?.to)

    const navigate: NavigateFunction = useNavigate()
    const goToCarDetails = (e: React.MouseEvent<HTMLTableCellElement>, car: Car) => {
        navigate(getAdminNavigationPath(adminRouteAlias.parts.location) + `?carId=${car.id}`)
        dispatch({
            type: storeDispatchTypes.setCarFilterParts,
            value: {
                car: {
                    carId: [car.id],
                    markTitle: car.markTitle,
                    modelTitle: car.modelTitle,
                    year: car.year,
                },
            },
        })
    }

    const goToCarSoldPartList = (e: React.MouseEvent<HTMLTableCellElement>, car: Car) => {
        navigate(getAdminNavigationPath(adminRouteAlias.soldParts.location) + `?carId=${car.id}`)
        dispatch({
            type: storeDispatchTypes.setCarFilterParts,
            value: {
                car: {
                    carId: [car.id],
                    markTitle: car.markTitle,
                    modelTitle: car.modelTitle,
                    year: car.year,
                },
            },
        })
    }

    const handleRowClick = useCallback(
        (car: Car) => {
            openEditModal?.(car)
        },
        [openEditModal],
    )
    const handleOpenDeleteModal = useCallback(
        (car: Car) => {
            openDeleteModal?.(car)
        },
        [openDeleteModal],
    )

    useEffect(() => {
        const filtered = canShowFinanceInfo
            ? columns
            : columns.filter(col => col.id !== 'price' && col.id !== 'sumSoldParts')

        setLocalColumns(filtered)
    }, [canShowFinanceInfo, columns])

    return (
        <Table striped hover>
            <thead>
                <tr>
                    {localColumns.map(column => {
                        switch (column.id) {
                            case 'donorId':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(55)}>
                                        Номер
                                        <ColumnSort
                                            iconType={SortIconType.NUMERIC}
                                            sortField={SortFieldCars.DONOR_ID}
                                            slice={'carList'}
                                            reverseSort
                                        />
                                    </th>
                                )
                            case 'photo':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(100)}>
                                        Фото
                                    </th>
                                )
                            case 'markTitle':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(100)}>
                                        <TableHeadMarkSearchCars />
                                    </th>
                                )
                            case 'modelTitle':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(200)}>
                                        <TableHeadModelSearchCars />
                                    </th>
                                )
                            case 'year':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(75)}>
                                        Год
                                        <ColumnSort
                                            iconType={SortIconType.NUMERIC}
                                            sortField={SortFieldCars.YEAR}
                                            slice={'carList'}
                                        />
                                    </th>
                                )
                            case 'modificationTitle':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(155)}>
                                        <TableHeadModificationSearchCars />
                                    </th>
                                )
                            case 'bodyTypeTitle':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(150)}>
                                        <TableHeadBodyTypeSearchCars />
                                    </th>
                                )
                            case 'mileage':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(88)}>
                                        <TableHeadMileageRangeCars
                                            initialFromVal={mileageFrom}
                                            initialToVal={mileageTo}
                                        />
                                    </th>
                                )
                            case 'price':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(96)}>
                                        <TableHeadPriceRangeCars
                                            initialFromVal={priceFrom}
                                            initialToVal={priceTo}
                                            initialTableHead={'Закуп'}
                                        />
                                    </th>
                                )
                            case 'sumSoldParts':
                                return (
                                    <th
                                        key={column.id}
                                        scope="col"
                                        style={colWidth(55)}
                                        className={styles.alignRightCell}
                                    >
                                        Продажи
                                    </th>
                                )
                            case 'createdAt':
                                return (
                                    <th
                                        key={column.id}
                                        scope="col"
                                        style={colWidth(113)}
                                        className={styles.alignRightCell}
                                    >
                                        Создано
                                        <ColumnSort
                                            iconType={SortIconType.NUMERIC}
                                            sortField={SortFieldCars.CREATED_AT}
                                            slice={'carList'}
                                            reverseSort
                                        />
                                    </th>
                                )
                            case 'partsCnt':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(79)}>
                                        <TableHeadPartsCountRangeCars
                                            initialFromVal={partsCntFrom}
                                            initialToVal={partsCntTo}
                                        />
                                    </th>
                                )
                            case 'actions':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(60)}>
                                        Действия
                                    </th>
                                )
                            case 'description':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(200)}>
                                        Описание
                                    </th>
                                )
                            case 'steeringType':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(60)}>
                                        Руль
                                    </th>
                                )
                            case 'equipment':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(80)}>
                                        Комплектация
                                    </th>
                                )
                            case 'bodycars':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(100)}>
                                        Модель кузова
                                    </th>
                                )
                            case 'engine':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(100)}>
                                        Модель двигателя
                                    </th>
                                )
                            default:
                                return (
                                    <td key={column.id} scope="row">
                                        Неизвестный столбец
                                    </td>
                                )
                        }
                    })}
                </tr>
            </thead>

            {isLoading || !cars ? (
                <ListSkeleton rowCount={9} columnCount={localColumns.length} marginVerticalRem={0.21} />
            ) : (
                <tbody>
                    {cars.map((car, index) => (
                        <tr key={index} className={styles.row}>
                            {localColumns.map(column => {
                                switch (column.id) {
                                    case 'donorId':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => handleRowClick(car)}>
                                                {car.extraData?.donorId || ''}
                                            </td>
                                        )
                                    case 'photo':
                                        return (
                                            <td key={column.id} scope="row">
                                                <CellThumbCarousel pictures={car.images} />
                                            </td>
                                        )
                                    case 'markTitle':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => handleRowClick(car)}>
                                                {car.markTitle}
                                            </td>
                                        )
                                    case 'modelTitle':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => handleRowClick(car)}>
                                                {car.modelTitle}
                                            </td>
                                        )
                                    case 'year':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => handleRowClick(car)}>
                                                {car.year}
                                            </td>
                                        )
                                    case 'modificationTitle':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => handleRowClick(car)}>
                                                {car.modificationTitle ? (
                                                    <div>
                                                        <Badge
                                                            text={car.modificationTitle}
                                                            color="#ddd"
                                                            fontColor="#555"
                                                        />
                                                    </div>
                                                ) : (
                                                    ''
                                                )}
                                                {car.vinCode ? (
                                                    <div>
                                                        <Badge text={'vin: ' + car.vinCode} color="#aaa" />
                                                    </div>
                                                ) : (
                                                    ''
                                                )}
                                            </td>
                                        )
                                    case 'bodyTypeTitle':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => handleRowClick(car)}>
                                                {car.bodyTypeTitle}
                                            </td>
                                        )
                                    case 'mileage':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => handleRowClick(car)}>
                                                {car.mileage ? car.mileage.toLocaleString('ru-RU') : ''}
                                            </td>
                                        )
                                    case 'price':
                                        return (
                                            <td
                                                key={column.id}
                                                scope="row"
                                                onClick={e => goToCarDetails(e, car)}
                                                className={styles.alignRightCell}
                                            >
                                                {car.price ? car.price.toLocaleString('ru-RU') : ''}
                                            </td>
                                        )
                                    case 'sumSoldParts':
                                        return (
                                            <td
                                                key={column.id}
                                                scope="row"
                                                onClick={e => goToCarSoldPartList(e, car)}
                                                className={styles.alignRightCell}
                                            >
                                                {car.sumSoldParts ? (
                                                    <CellSoldPercent
                                                        soldPercent={car.soldPartsPercent}
                                                        carPrice={car.price}
                                                        sumSoldParts={car.sumSoldParts}
                                                        leftPartsSum={car.leftPartsSum}
                                                    />
                                                ) : (
                                                    ''
                                                )}
                                            </td>
                                        )
                                    case 'createdAt':
                                        return (
                                            <td
                                                key={column.id}
                                                scope="row"
                                                onClick={() => handleRowClick(car)}
                                                className={styles.alignRightCell}
                                            >
                                                <div>
                                                    <Badge
                                                        text={formatDate(car.createdAt)}
                                                        color="#ddd"
                                                        fontColor="#555"
                                                    />
                                                </div>
                                                {+countDaysInStock(car.createdAt) > 10 ? (
                                                    <div>
                                                        <Badge
                                                            text={
                                                                countDaysInStock(car.createdAt) +
                                                                ' ' +
                                                                num_word(+countDaysInStock(car.createdAt), [
                                                                    'день',
                                                                    'дня',
                                                                    'дней',
                                                                ])
                                                            }
                                                            color="#eee"
                                                            fontColor="#777"
                                                        />
                                                    </div>
                                                ) : (
                                                    ''
                                                )}
                                            </td>
                                        )
                                    case 'partsCnt':
                                        return (
                                            <td
                                                key={column.id}
                                                scope="row"
                                                onClick={() => handleRowClick(car)}
                                                className={styles.alignRightCell}
                                            >
                                                {car.partsCnt}
                                            </td>
                                        )
                                    case 'actions':
                                        return (
                                            <td key={column.id} scope="row">
                                                <CellActionsCars
                                                    car={car}
                                                    isSafari={browserName === 'Safari'}
                                                    editModal={() => handleRowClick(car)}
                                                    deleteModal={() => handleOpenDeleteModal(car)}
                                                />
                                            </td>
                                        )
                                    case 'description':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => handleRowClick(car)}>
                                                {car?.description}
                                            </td>
                                        )
                                    case 'steeringType':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => handleRowClick(car)}>
                                                {car?.extraData?.steeringType === 'right' ? 'Правый' : 'Левый'}
                                            </td>
                                        )
                                    case 'equipment':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => handleRowClick(car)}>
                                                {car?.extraData?.equipment}
                                            </td>
                                        )
                                    case 'bodycars':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => handleRowClick(car)}>
                                                {car?.extraData?.bodycars}
                                            </td>
                                        )
                                    case 'engine':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => handleRowClick(car)}>
                                                {car?.extraData?.engine}
                                            </td>
                                        )
                                    default:
                                        return (
                                            <td key={column.id} scope="row">
                                                Неизвестный столбец
                                            </td>
                                        )
                                }
                            })}
                        </tr>
                    ))}
                </tbody>
            )}
        </Table>
    )
}
